import { designVariables } from "@studio3t/design-variables";
import { Icon, IconSVG } from "@studio3t/generic-components";
import styled, { useTheme } from "styled-components";
import { MoreTooltip } from "@/components/MoreTooltip";
import { formatPipeline } from "@/pages/pipelineHelpers";
import type { JobMetadata } from "@/types/api";

const SINGLE_LINE = designVariables.type.singleLine;
const BADGE = designVariables.ui.badge;
const BUTTON = designVariables.ui.button;
const TABLE = designVariables.ui.table;
const RAINBOW = designVariables.primitives.rainbow.alpha;

export type StageVariant = "src" | "mw" | "sink";
const STAGE_COLORS: Record<StageVariant, string> = {
  src: RAINBOW.yellow,
  mw: RAINBOW.orange,
  sink: RAINBOW.turquoise,
};

const STAGE_ICONS: Record<StageVariant, Icon> = {
  src: Icon.Input,
  mw: Icon.Transform,
  sink: Icon.Output,
};

const STAGE_LABELS: Record<StageVariant, string> = {
  src: "SRC",
  mw: "MW",
  sink: "SINK",
};

// The colored SRC/MW/SINK chip used across the app (Pipelines list, service
// tables, /newStudio explorer role badges).
export function StageTag({ variant }: { variant: StageVariant }) {
  const theme = useTheme();
  return (
    <StageBadge $variant={variant}>
      <IconSVG
        icon={STAGE_ICONS[variant]}
        size={BADGE.icon}
        colors={{ default: theme.badgeSecondaryMinimalColor }}
      />
      {STAGE_LABELS[variant]}
    </StageBadge>
  );
}

// Hidden-stage counter per Figma 13387-52572: a divider followed by a
// subtle-button-styled "+ N" (not a colored stage badge). Hovering it lists the
// hidden stages in a tooltip headed by the stage type (e.g. "Middleware", "Sink").
function MoreStages({
  hidden,
  heading,
}: {
  hidden: string[];
  heading: string;
}) {
  if (hidden.length === 0) return null;
  return (
    <>
      <StageDivider />
      <MoreTooltip heading={heading} items={hidden}>
        <MoreCount>+ {hidden.length}</MoreCount>
      </MoreTooltip>
    </>
  );
}

// The SRC → (MW …) → SINK flow for a pipeline row, shared by the Pipelines
// list, the single-service "Used by pipelines" table and the Workspaces list
// (which only has the pipeline, not a full job). Extra middlewares/sinks
// collapse into a "+ N" tooltip. Needs useTheme, so it's a component rather
// than an inline cell renderer.
export function PipelineFlowCell({
  job,
}: {
  job: Pick<JobMetadata, "pipeline">;
}) {
  const { source, middlewares, sinks } = formatPipeline(job);

  return (
    <PipelineFlow>
      <StageTag variant="src" />
      <StageText>{source} →</StageText>
      {middlewares.length > 0 && (
        <>
          <StageTag variant="mw" />
          <StageText>{middlewares[0]}</StageText>
          {/* Extra middlewares collapse into a divider + "+ N" (Figma
              13078-31995), then the arrow leads on to the sink. */}
          <MoreStages hidden={middlewares.slice(1)} heading="Middleware" />
          <FlowArrow>→</FlowArrow>
        </>
      )}
      <StageTag variant="sink" />
      <StageText>{sinks[0] ?? "—"}</StageText>
      <MoreStages hidden={sinks.slice(1)} heading="Sink" />
    </PipelineFlow>
  );
}

const PipelineFlow = styled.div`
  display: flex;
  align-items: center;
  gap: ${TABLE.tbody.cell.gap}px;
  padding-left: ${TABLE.tbody.cell.gap}px;
  min-width: 0;
`;

const StageBadge = styled.span<{ $variant: StageVariant }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${BADGE.gap}px;
  height: ${BADGE.height}px;
  padding: 0 ${BADGE.paddingw}px;
  border-radius: ${BADGE.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1;
  white-space: nowrap;
  color: ${(props) => props.theme.badgeSecondaryMinimalColor};
  background: ${(props) => STAGE_COLORS[props.$variant]};
  border: 1px solid ${(props) => STAGE_COLORS[props.$variant]};
`;

const StageText = styled.span`
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Labels shrink at different rates depending on their content, but never
     below ~9 chars — after that they ellipse instead of vanishing. */
  min-width: 80px;
`;

// Separator between stages; unlike StageText it never shrinks or ellipses.
// Spacing to neighbours comes from PipelineFlow's gap (Table/tbody/cell/gap).
const FlowArrow = styled.span`
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  flex-shrink: 0;
`;

// 1px full-height rule before a "+ N" counter (Figma: semantic/borderlight).
const StageDivider = styled.span`
  align-self: stretch;
  width: 1px;
  flex-shrink: 0;
  background: ${(props) => props.theme.semanticBorderLight};
`;

// "+ N" hidden-stage counter, styled like a subtle button per Figma
// (transparent background, disabled-subtle text color).
const MoreCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${BUTTON.s.size}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${(props) => props.theme.buttonSubtleDisabledText};
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme.semanticTextBody};
  }
`;
